import React, { useState } from "react";

/**
 * CompanyLogo — Phase 8.LP.LR
 * ---------------------------
 * Shared, size-tunable company-logo tile used across:
 *   * Landingpage Popular Tickers
 *   * AI Market Snapshot cards
 *   * Analyze loading screen
 *   * Watchlist rows/cards
 *   * Portfolio holdings table
 *
 * Rendering rules (per Phase 8.LP.LR contract):
 *   * The original provider image is NEVER modified, cropped,
 *     recolored, masked, or zoomed.
 *   * `object-contain` preserves aspect ratio.
 *   * Provider white backgrounds are ACCEPTED as-is (bordered
 *     contained style matches the current Analyze header look).
 *   * Missing-logo fallback shows uppercase initials centered
 *     inside a neutral dark tile.
 *   * `onError` gracefully switches to initials so a broken logo
 *     URL never leaves a broken image icon in the UI.
 */

// Size presets — pick one per surface so the whole app looks
// consistent without callers hand-rolling class strings.
const SIZE = {
  xs: {
    outer: "h-6 w-6 rounded-md border border-border/70 bg-muted/40",
    img: "h-full w-full object-contain p-0.5",
    initials: "text-[8px] font-semibold tracking-tight",
  },
  sm: {
    outer: "h-8 w-8 rounded-lg border border-border/70 bg-muted/40",
    img: "h-full w-full object-contain p-1",
    initials: "text-[10px] font-semibold tracking-tight",
  },
  md: {
    outer: "h-10 w-10 rounded-xl border border-border/70 bg-muted/40",
    img: "h-full w-full object-contain p-1",
    initials: "text-xs font-semibold tracking-tight",
  },
  lg: {
    outer:
      "h-12 w-12 sm:h-14 sm:w-14 rounded-2xl border border-border/70 bg-muted/40",
    img: "h-full w-full object-contain p-1.5",
    initials: "text-sm sm:text-base font-semibold tracking-tight",
  },
};

// Only allow safe absolute HTTPS URLs (matches the check in
// CompanyIdentityHeader). Anything else falls through to initials.
function isSafeLogoUrl(url) {
  if (!url || typeof url !== "string") return false;
  const s = url.trim();
  if (!s) return false;
  try {
    const u = new URL(s);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}

function computeInitials(ticker, name) {
  const source = (name && String(name)) || (ticker && String(ticker)) || "";
  if (!source) return "";
  const words = source
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

export function CompanyLogo({
  ticker,
  name,
  logoUrl,
  size = "sm",
  className = "",
  testId,
  fallbackText, // optional override — when provided, used as the fallback tile content instead of computed initials
}) {
  const [broken, setBroken] = useState(false);
  const preset = SIZE[size] || SIZE.sm;
  const canRenderImage = !broken && isSafeLogoUrl(logoUrl);
  const initials = fallbackText != null ? String(fallbackText) : computeInitials(ticker, name);
  const alt = (name && `${name} logo`) || (ticker && `${ticker} logo`) || "Company logo";

  return (
    <div
      className={`relative shrink-0 overflow-hidden flex items-center justify-center ${preset.outer} ${className}`}
      data-testid={testId || (ticker ? `company-logo-${String(ticker).toUpperCase()}` : "company-logo")}
      data-logo-state={canRenderImage ? "image" : "fallback"}
      aria-label={alt}
    >
      {canRenderImage ? (
        <img
          src={logoUrl}
          alt={alt}
          loading="lazy"
          className={preset.img}
          onError={() => setBroken(true)}
          data-testid="company-logo-img"
        />
      ) : (
        <span
          aria-hidden
          className={`${preset.initials} text-foreground/80 select-none`}
          data-testid="company-logo-fallback"
        >
          {initials || "?"}
        </span>
      )}
    </div>
  );
}

export default CompanyLogo;
